import { effect, inject, Injectable, signal } from '@angular/core';
import { AuthStore } from '@stores/auth.store';
import { TopicType } from '../enums/topic-type';
import { GlobalConfig } from '@interfaces/global-config';
import { WebSocketService } from '@services/web-socket.service';
import { UserMessageResponse } from '@interfaces/user/user-message.response';
import { UserRole } from '../enums/user-role';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '@services/toast.service';
import { User, UserBuilder } from '@interfaces/user/user';
import dayjs from 'dayjs';
import { UserAvailabilityStatus } from '../enums/user-availability-status';
import { HttpStatusCode } from '@angular/common/http';
import { SettingsService } from '@services/settings.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authStore = inject(AuthStore);
  isLoading = this.authStore.isLoading;
  statusCode = this.authStore.statusCode;
  user = this.authStore.user;
  role = this.authStore.role;
  isUserAuthenticated = this.authStore.isAuthenticated;
  private webSocketService = inject(WebSocketService);
  private _uuid = this.webSocketService.uuid;
  private settingsService = inject(SettingsService);
  lastPingTime = signal<string>(dayjs().toISOString());
  connectionWasLost: boolean = false;
  interval: any;
  get uuid() {
    return this._uuid;
  }
  private router = inject(Router);
  private toastService = inject(ToastService);
  constructor() {
    effect(() => {
      const hasUser = !!this.authStore.user()?.id;
      if (hasUser && !this.interval) {
        this.initCheckingWithServer();
      }
      if (!hasUser && this.interval) {
        this.resetCheckingWithServer();
      }
    });
  }

  updateUserGlobalConfig(response: GlobalConfig) {
    this.authStore.updateUserGlobalConfig(response);
  }

  updateAvailabilityStatus(status: UserAvailabilityStatus) {
    this.authStore.updateAvailabilityStatus(status);
  }

  setUser(message: UserMessageResponse) {
    if (message.status) {
      const isRolePresent = [
        UserRole.UNIT_ADMINISTRATOR,
        UserRole.PORTAL_ADMINISTRATOR,
        UserRole.LOCATING_OPERATOR,
        UserRole.SECURITY_MANAGER,
        UserRole.SECURITY_COMMAND_CENTRE,
      ].includes(message.role.ekoUserRole);

      if (!isRolePresent) {
        this.toastService.showInfoMessage('You are not allowed to log in');
        this.authStore.setErrorStatusCode(HttpStatusCode.Forbidden);
      } else {
        const username = message.username;

        const popupNotifications =
          message?.notificationSettings?.popupNotifications ??
          (message as any)?.popupNotifications ??
          [];

        const userDeviceId = message.deviceId ?? (message as any)?.device;

        if (userDeviceId) {
          sessionStorage.setItem('deviceId', userDeviceId);
        }

        const user: User = UserBuilder.withUsername(username)
          .withAvailabilityStatus(message.availabilityStatus)
          .withUserId(message.id)
          .withName(message.name)
          .withAssignedCodes(message.assignedCodes)
          .withToken(message?.token)
          .withProfilePicture(message?.profilePicture)
          .withRole(message.role.ekoUserRole)
          .withKey(message.key)
          .withTtl(dayjs().add(12, 'hours').toISOString())
          .withGlobalConfiguration(message.globalConfiguration)
          .withDepartments(message.departments)
          .withPrimaryDepartment(message.primaryDepartment ?? '')
          .withNotificationSettings({ popupNotifications })
          .build();

        this.resetCheckingWithServer();
        this.initCheckingWithServer();

        this.authStore.login(user);

        const role = message.role.ekoUserRole;
        if (UserRole.SECURITY_COMMAND_CENTRE === role) {
          this.router.navigateByUrl('/floor-plan');
        } else if (UserRole.PORTAL_ADMINISTRATOR === role) {
          this.router.navigateByUrl('/roles-and-permissions');
        } else if (UserRole.SECURITY_MANAGER === role) {
          this.router.navigateByUrl('/staff-scheduler');
        } else {
          this.router.navigateByUrl('/dashboard');
        }
      }
    } else {
      const lockedOutMessage =
        'It looks like you have been locked out due to too many unsuccessful login attempts. Please contact your administrator or technical support.';
      if (message.message === lockedOutMessage) {
        this.toastService.showInfoMessage(message.message);
      }
      this.authStore.setErrorStatusCode(HttpStatusCode.Unauthorized);
    }
    this.setIsLoading(false);
  }

  initCheckingWithServer() {
    this.resetCheckingWithServer();
    this.interval = setInterval(() => {
      const lastPingTime = this.lastPingTime();
      const currentTime = dayjs();
      const timeDiff = currentTime.diff(lastPingTime, 'seconds');
      const isTimeOut = timeDiff > 25;

      if (!isTimeOut && this.connectionWasLost) {
        this.toastService.removeToasts();
        this.connectionWasLost = false;

        this.toastService.showInfoMessage('front.toast.connection-restored');
      }
      if (isTimeOut && this.toastService.getToasts().length < 1) {
        this.toastService.showInfoMessage(
          'front.toast.lost-connection-message',
        );
        this.connectionWasLost = true;

        this.logOut();
        this.router.navigateByUrl('/login');
      }
    }, 1000);
  }

  resetCheckingWithServer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
      this.connectionWasLost = false;
    }
  }

  logOut() {
    this.settingsService.reset();
    this.authStore.logOut();
  }

  loginWithEmailAndPassword(loginForm: FormGroup) {
    const { username, password } = loginForm.getRawValue();
    const message: object = {
      key: this.uuid,
      device: this.uuid,
      username: username,
      password: password,
      platform: 'web',
    };
    this.authStore.setPassword(password);
    this.webSocketService.send({
      topic: TopicType.USER_LOGIN_REQUEST,
      message,
    });

    setTimeout(() => {
      if (this.isLoading()) {
        this.toastService.showInfoMessage(
          'front.toast.lost-connection-message-login',
        );
        this.setIsLoading(false);
      }
    }, 15000);
  }

  setIsLoading(isLoading: boolean) {
    this.authStore.setIsLoading(isLoading);
  }

  refreshToken() {
    const { username, password } = this.authStore.user();
    const message: object = {
      key: this.uuid,
      device: this.uuid,
      username: username,
      password: password,
      platform: 'web',
    };
    this.webSocketService.send({
      topic: TopicType.USER_LOGIN_REQUEST,
      message,
    });
  }
}
